import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import StatCard from "../../components/Kids/StatCard";
import Skeleton from "../../components/Kids/Skeleton";

const API   = process.env.REACT_APP_API_URL;
const TAPI  = `${API}/api/edolutions/school-teacher`;
const token = () => JSON.parse(localStorage.getItem("edo-admin"))?.token;

const SUBJECTS   = ["math", "english", "science", "arabic", "art", "coding", "general"];
const GAME_TYPES = ["quiz", "puzzle", "drag_drop", "typing", "drawing", "story", "flashcard", "tracing"];
const DIFFS      = ["easy", "medium", "hard"];
const PLANS_REQ  = ["starter", "pro", "premium"];

const inp  = "w-full px-3 py-2 rounded-xl border border-gray-200 outline-none focus:border-[#EE5F2B] text-sm";
const btn  = "px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200";
const card = "bg-white rounded-2xl border border-[#EEF1F5] shadow-sm";

function L({ label, children }) {
  return <div><label className="block text-sm font-semibold text-gray-600 mb-1">{label}</label>{children}</div>;
}
function Sel({ value, opts, onChange }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} className={inp}>
      {opts.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

const NAV = [
  { key: "dashboard", icon: "📊", label: "Dashboard" },
  { key: "students",  icon: "🧒", label: "My Students" },
  { key: "games",     icon: "🎮", label: "My Games" },
];

export default function SchoolTeacher() {
  const [tab, setTab] = useState("dashboard");
  const [selectedChild, setSelectedChild] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const auth = { headers: { Authorization: `Bearer ${token()}` } };
  const adminData = JSON.parse(localStorage.getItem("edo-admin") || "{}");
  const teacherName = adminData?.admin?.name || "Teacher";

  useEffect(() => {
    const raw = localStorage.getItem("edo-admin");
    if (!raw) { navigate("/admin"); return; }
    try {
      const data = JSON.parse(raw);
      if (Number(data.admin?.role_id) !== 7) navigate("/admins/home");
    } catch { navigate("/admin"); }
  }, [navigate]);

  const logout = async () => {
    try { await axios.post(`${API}/api/edolutions/admin/logout`, {}, auth); } catch {}
    localStorage.removeItem("edo-admin");
    window.location.href = "/admin";
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA] flex">
      {sidebarOpen && <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <aside className={`
        fixed lg:sticky top-0 start-0 z-50 lg:z-auto
        w-64 h-screen bg-white border-e border-[#EEF1F5] shadow-lg lg:shadow-none
        flex flex-col transition-transform duration-300
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full rtl:translate-x-full lg:translate-x-0"}
      `}>
        <div className="h-1 w-full bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]" />
        <div className="p-5 border-b border-[#EEF1F5]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#EE5F2B] to-[#2A3A54] flex items-center justify-center text-white text-lg font-bold shrink-0">
              {teacherName.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <h1 className="text-sm font-bold text-[#2A3A54] truncate">{teacherName}</h1>
              <p className="text-xs text-gray-400">Class Teacher</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {NAV.map(({ key, icon, label }) => (
            <button key={key} onClick={() => { setTab(key); setSelectedChild(null); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                tab === key ? "bg-gradient-to-r from-[#FFF0EA] to-[#FFF6F2] text-[#EE5F2B] shadow-sm border border-[#FBE2CF]" : "text-gray-600 hover:bg-[#F7F8FA] hover:text-gray-800"
              }`}>
              <span className="text-lg">{icon}</span>{label}
            </button>
          ))}
        </nav>

        <div className="p-3 border-t border-[#EEF1F5]">
          <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-[#E5484D] hover:bg-red-50 transition">
            <span>🚪</span> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <header className="lg:hidden bg-white border-b border-[#EEF1F5] px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <button onClick={() => setSidebarOpen(true)} className="text-2xl text-gray-600">☰</button>
          <h1 className="text-sm font-bold text-[#2A3A54]">{NAV.find(n => n.key === tab)?.label}</h1>
          <div className="w-8" />
        </header>

        <div className="hidden lg:flex items-center justify-between px-8 py-5 border-b border-[#EEF1F5] bg-white/60 backdrop-blur-sm">
          <div>
            <h2 className="text-xl font-bold text-[#2A3A54]">{NAV.find(n => n.key === tab)?.icon} {NAV.find(n => n.key === tab)?.label}</h2>
            <p className="text-sm text-gray-400 mt-0.5">Manage your class</p>
          </div>
        </div>

        <div className="p-4 md:p-6 lg:p-8">
          {tab === "dashboard" && <Dashboard auth={auth} />}
          {tab === "students" && !selectedChild && <StudentList auth={auth} onViewProgress={setSelectedChild} />}
          {tab === "students" && selectedChild && <StudentProgress auth={auth} childId={selectedChild} onBack={() => setSelectedChild(null)} />}
          {tab === "games" && <Games auth={auth} />}
        </div>
      </main>
    </div>
  );
}

/* ═══════════════════════════ DASHBOARD ═══════════════════════════ */
function Dashboard({ auth }) {
  const [stats, setStats] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${TAPI}/dashboard`, auth)
      .then(r => setStats(r.data.data))
      .catch((e) => { if (e.response?.status === 404) setNotFound(true); else toast.error("Failed to load dashboard."); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (notFound) return (
    <div className={`${card} text-center py-16`}>
      <div className="text-5xl mb-3">🏷️</div>
      <p className="text-gray-500 font-semibold">No class assigned yet</p>
      <p className="text-gray-400 text-sm mt-1">Ask your school admin to assign you a class.</p>
    </div>
  );

  if (!stats) return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className={`${card} flex flex-col items-center gap-2 px-4 py-4`}>
          <Skeleton className="w-6 h-6" rounded="8px" />
          <Skeleton className="w-12 h-6" rounded="6px" />
          <Skeleton className="w-16 h-3" rounded="4px" />
        </div>
      ))}
    </div>
  );

  const cards = [
    ["🧒", "My Students", stats.total_students],
    ["🎮", "Games Played", stats.games_played],
    ["⭐", "Total XP", stats.total_xp],
    ["🎲", "My Games", stats.my_games],
  ];

  return (
    <div className="space-y-6">
      {stats.classes?.length > 0 && (
        <div className={`${card} p-5`}>
          <h3 className="font-bold text-[#2A3A54] mb-2">My Classes</h3>
          <div className="flex flex-wrap gap-2">
            {stats.classes.map(c => <span key={c.id} className="text-sm px-3 py-1 rounded-full bg-blue-50 text-blue-600 font-medium">{c.name}</span>)}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(([icon, label, value]) => (
          <StatCard key={label} emoji={icon} value={value ?? 0} label={label} className="hover:shadow-md transition-shadow" />
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className={`${card} p-5`}>
          <h3 className="font-bold text-[#2A3A54] mb-4">🏆 Top Students</h3>
          {stats.top_students?.length > 0 ? (
            <div className="space-y-2">
              {stats.top_students.map((s, i) => (
                <div key={i} className="flex items-center justify-between bg-[#F7F8FA] rounded-xl px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white bg-gray-400">{i + 1}</span>
                    <span className="text-sm font-semibold text-gray-700">{s.name}</span>
                  </div>
                  <div className="flex gap-3 text-xs">
                    <span className="text-gray-500">Lv {s.level}</span>
                    <span className="font-bold text-[#2A3A54]">{s.xp} XP</span>
                    {s.streak > 0 && <span className="text-orange-500">🔥{s.streak}</span>}
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-gray-400 text-sm text-center py-6">No student data yet</p>}
        </div>

        <div className={`${card} p-5`}>
          <h3 className="font-bold text-[#2A3A54] mb-4">⏱ Recent Activity</h3>
          {stats.recent_activity?.length > 0 ? (
            <div className="space-y-2 max-h-[280px] overflow-y-auto">
              {stats.recent_activity.map((a, i) => (
                <div key={i} className="flex items-center justify-between bg-[#F7F8FA] rounded-xl px-4 py-2.5">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-700 truncate">{a.child_name}</div>
                    <div className="text-xs text-gray-400 truncate">{a.game_name} · <span className="capitalize">{a.subject}</span></div>
                  </div>
                  <div className="text-end shrink-0 ms-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${a.status === "completed" ? "bg-[#DFF5E6] text-[#22874E]" : "bg-yellow-100 text-yellow-700"}`}>
                      {a.status === "completed" ? `✓ ${a.best_score}%` : "in progress"}
                    </span>
                    <div className="text-[10px] text-gray-400 mt-0.5">{a.last_played}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-gray-400 text-sm text-center py-6">No activity yet</p>}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════ STUDENT LIST ═══════════════════════════ */
function StudentList({ auth, onViewProgress }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const r = await axios.get(`${TAPI}/students${q ? `?search=${encodeURIComponent(q)}` : ""}`, auth);
      setStudents(r.data.data || []);
    } catch { toast.error("Failed to load students."); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <SkTable cols={6} rows={6} />;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex gap-2 flex-1">
          <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && load(search)}
            placeholder="Search by student name..." className="flex-1 max-w-md px-4 py-2.5 rounded-xl border border-gray-200 outline-none focus:border-[#EE5F2B] text-sm" />
          <button onClick={() => load(search)} className={`${btn} bg-[#2A3A54] text-white hover:bg-[#1e2d42]`}>Search</button>
        </div>
        <div className="text-sm text-gray-400 self-center">{students.length} student{students.length !== 1 ? "s" : ""}</div>
      </div>

      {students.length === 0 ? (
        <div className={`${card} text-center py-16`}>
          <div className="text-5xl mb-3">🧒</div>
          <p className="text-gray-400 text-sm">No students in your class yet.</p>
        </div>
      ) : (
        <div className={`${card} overflow-x-auto`}>
          <table className="w-full text-sm">
            <thead className="bg-[#F7F8FA]/80 text-gray-500 text-start text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3">Student</th>
                <th className="px-4 py-3">Grade</th>
                <th className="px-4 py-3">Level / XP</th>
                <th className="px-4 py-3">Streak</th>
                <th className="px-4 py-3">Games</th>
                <th className="px-4 py-3 text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {students.map(s => (
                <tr key={s.id} className={`border-t border-[#EEF1F5] hover:bg-[#F7F8FA]/50 transition ${!s.is_active ? "opacity-50" : ""}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-800">{s.name}</div>
                    {s.nickname && <div className="text-xs text-gray-400">{s.nickname}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{s.grade_label}</td>
                  <td className="px-4 py-3">
                    <span className="font-semibold text-[#2A3A54]">Lv {s.level}</span>
                    <span className="text-xs text-gray-400 ms-1">· {s.total_xp} XP</span>
                  </td>
                  <td className="px-4 py-3"><span className="text-orange-500 font-semibold">🔥 {s.streak_days}</span></td>
                  <td className="px-4 py-3 text-gray-600">{s.games_completed}</td>
                  <td className="px-4 py-3 text-end">
                    <button onClick={() => onViewProgress(s.id)} className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-green-50 text-green-600 hover:bg-green-100">
                      Progress
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════ STUDENT PROGRESS ═══════════════════════════ */
function StudentProgress({ auth, childId, onBack }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${TAPI}/progress/${childId}`, auth)
      .then(r => setData(r.data.data))
      .catch(() => toast.error("Failed to load progress."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId]);

  if (!data) return <SkTable cols={7} rows={5} />;

  return (
    <div className="space-y-5">
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-[#2A3A54] flex items-center gap-1 font-medium">← Back to students</button>

      <div className={`${card} p-6`}>
        <h2 className="text-xl font-bold text-[#2A3A54] mb-1">{data.child?.name}</h2>
        <p className="text-sm text-gray-500">{data.child?.grade_label}</p>
        {data.rewards && (
          <div className="flex flex-wrap gap-4 mt-4">
            {[["🏆", "Level", data.rewards.level], ["⭐", "XP", data.rewards.total_xp], ["🪙", "Coins", data.rewards.coins], ["🔥", "Streak", `${data.rewards.streak_days} days`]].map(([icon, label, val]) => (
              <div key={label} className="bg-[#F7F8FA] rounded-xl px-4 py-2.5 text-sm">
                <span className="me-1">{icon}</span><span className="text-gray-400">{label}:</span> <span className="font-bold text-[#2A3A54]">{val}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {(!data.progress || data.progress.length === 0) ? (
        <div className={`${card} text-center py-14 text-gray-400`}><div className="text-4xl mb-2">🎮</div>No game history yet.</div>
      ) : (
        <div className={`${card} overflow-x-auto`}>
          <table className="w-full text-sm">
            <thead className="bg-[#F7F8FA]/80 text-gray-500 text-start text-xs uppercase tracking-wider">
              <tr><th className="px-4 py-3">Game</th><th className="px-4 py-3">Subject</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Best Score</th><th className="px-4 py-3">XP</th><th className="px-4 py-3">Attempts</th><th className="px-4 py-3">Last Played</th></tr>
            </thead>
            <tbody>
              {data.progress.map((g, i) => (
                <tr key={i} className="border-t border-[#EEF1F5]">
                  <td className="px-4 py-3 font-semibold text-gray-800">{g.game || "—"}</td>
                  <td className="px-4 py-3 capitalize text-gray-600">{g.subject || "—"}</td>
                  <td className="px-4 py-3"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${g.status === "completed" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>{g.status}</span></td>
                  <td className="px-4 py-3 font-semibold text-[#2A3A54]">{g.best_score ?? "—"}</td>
                  <td className="px-4 py-3 text-gray-600">{g.xp_earned ?? 0}</td>
                  <td className="px-4 py-3 text-gray-600">{g.attempts ?? 0}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{g.last_played || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════ GAMES (scoped to my class) ═══════════════════════════ */
const BLANK_GAME = {
  name: "", slug: "", subject: "math", min_grade: 0, max_grade: 6,
  difficulty: "easy", game_type: "quiz", xp_reward: 10, coin_reward: 5,
  plan_required: "starter", is_active: true, content: null, class_id: "",
};

function Games({ auth }) {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${TAPI}/games`, auth);
      setGames(r.data.data || []);
    } catch { toast.error("Failed to load games."); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (g) => {
    try { await axios.put(`${TAPI}/games/${g.id}/toggle`, {}, auth); load(); }
    catch { toast.error("Couldn't toggle game."); }
  };

  const remove = async (g) => {
    if (!window.confirm(`Delete "${g.name}"? (If it has play history it will be deactivated instead.)`)) return;
    try {
      const r = await axios.delete(`${TAPI}/games/${g.id}`, auth);
      toast.success(r.data.message);
      load();
    } catch { toast.error("Couldn't delete game."); }
  };

  if (loading) return <SkTable cols={6} rows={5} />;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => setEditing({ ...BLANK_GAME })} className={`${btn} bg-[#EE5F2B] text-white hover:bg-orange-600`}>+ New Game</button>
      </div>

      {games.length === 0 ? (
        <div className={`${card} text-center py-16`}>
          <div className="text-5xl mb-3">🎮</div>
          <p className="text-gray-400 text-sm">You haven't created any games yet.</p>
        </div>
      ) : (
        <div className={`${card} overflow-x-auto`}>
          <table className="w-full text-sm">
            <thead className="bg-[#F7F8FA]/80 text-gray-500 text-start text-xs uppercase tracking-wider">
              <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Subject</th><th className="px-4 py-3">Grades</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Active</th><th className="px-4 py-3 text-end">Actions</th></tr>
            </thead>
            <tbody>
              {games.map(g => (
                <tr key={g.id} className={`border-t border-[#EEF1F5] hover:bg-[#F7F8FA]/50 transition ${!g.is_active ? "opacity-50" : ""}`}>
                  <td className="px-4 py-3"><div className="font-semibold text-gray-800">{g.name}</div><div className="text-xs text-gray-400 font-normal">{g.slug}</div></td>
                  <td className="px-4 py-3 capitalize">{g.subject}</td>
                  <td className="px-4 py-3">{g.min_grade}–{g.max_grade}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium">{g.game_type}</span></td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggle(g)} className={`text-xs font-semibold px-2.5 py-1 rounded-full ${g.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {g.is_active ? "On" : "Off"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-end whitespace-nowrap">
                    <button onClick={() => setEditing(g)} className="text-[#2A3A54] hover:underline text-xs font-semibold me-2">Edit</button>
                    <button onClick={() => remove(g)} className="text-red-500 hover:underline text-xs font-semibold">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <GameModal game={editing} auth={auth} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function GameModal({ game, auth, onClose, onSaved }) {
  const [form, setForm] = useState({ ...game });
  const [myClasses, setMyClasses] = useState([]);
  const [contentText, setContentText] = useState(game.content ? JSON.stringify(game.content, null, 2) : "");
  const [saving, setSaving] = useState(false);
  const isNew = !game.id;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    axios.get(`${TAPI}/dashboard`, auth).then(r => setMyClasses(r.data.data?.classes || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    if (!form.class_id) return toast.warn("Pick which of your classes this game is for.");
    let content = null;
    if (contentText.trim()) {
      try { content = JSON.parse(contentText); }
      catch { return toast.error("Content is not valid JSON."); }
    }
    setSaving(true);
    try {
      const payload = { ...form, content };
      if (isNew) await axios.post(`${TAPI}/games`, payload, auth);
      else       await axios.put(`${TAPI}/games/${game.id}`, payload, auth);
      toast.success(isNew ? "Game created." : "Game updated.");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.message || "Couldn't save game.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`${card} w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6`} onClick={e => e.stopPropagation()}>
        <h2 className="font-bold text-lg text-[#2A3A54] mb-4">{isNew ? "New Game" : `Edit: ${game.name}`}</h2>

        <div className="grid sm:grid-cols-2 gap-4">
          <L label="Name"><input className={inp} value={form.name} onChange={e => set("name", e.target.value)} /></L>
          <L label="Class"><Sel value={form.class_id || ""} opts={["", ...myClasses.map(c => String(c.id))]} onChange={v => set("class_id", v)} /></L>
          <L label="Subject"><Sel value={form.subject} opts={SUBJECTS} onChange={v => set("subject", v)} /></L>
          <L label="Game Type"><Sel value={form.game_type} opts={GAME_TYPES} onChange={v => set("game_type", v)} /></L>
          <L label="Min Grade"><input type="number" className={inp} value={form.min_grade} onChange={e => set("min_grade", +e.target.value)} /></L>
          <L label="Max Grade"><input type="number" className={inp} value={form.max_grade} onChange={e => set("max_grade", +e.target.value)} /></L>
          <L label="Difficulty"><Sel value={form.difficulty} opts={DIFFS} onChange={v => set("difficulty", v)} /></L>
          <L label="Plan Required"><Sel value={form.plan_required} opts={PLANS_REQ} onChange={v => set("plan_required", v)} /></L>
          <L label="XP Reward"><input type="number" className={inp} value={form.xp_reward} onChange={e => set("xp_reward", +e.target.value)} /></L>
          <L label="Coin Reward"><input type="number" className={inp} value={form.coin_reward} onChange={e => set("coin_reward", +e.target.value)} /></L>
        </div>

        <label className="flex items-center gap-2 mt-4 text-sm">
          <input type="checkbox" checked={!!form.is_active} onChange={e => set("is_active", e.target.checked)} className="rounded" />
          Active (visible to your class)
        </label>

        <div className="mt-4">
          <label className="block text-sm font-semibold text-gray-600 mb-1">Content (JSON)</label>
          <textarea rows={8} value={contentText} onChange={e => setContentText(e.target.value)}
            placeholder="Paste valid JSON content for this game type."
            className="w-full px-3 py-2 rounded-xl border border-gray-200 font-mono text-xs outline-none focus:border-[#EE5F2B]" />
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className={`${btn} text-gray-500 hover:bg-gray-100`}>Cancel</button>
          <button onClick={save} disabled={saving} className={`${btn} bg-[#EE5F2B] text-white hover:bg-orange-600 disabled:opacity-50`}>
            {saving ? "Saving…" : "Save Game"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════ LOADING SKELETON ═══════════════════════════ */
function SkTable({ cols = 5, rows = 5 }) {
  return (
    <div className={`${card} overflow-hidden`}>
      <div className="px-4 py-3 bg-[#F7F8FA]/80"><Skeleton className="h-3 w-24" rounded="4px" /></div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-4 px-4 py-3 border-t border-[#EEF1F5]">
          {Array.from({ length: cols }).map((_, c) => <Skeleton key={c} className="h-4 flex-1" rounded="6px" />)}
        </div>
      ))}
    </div>
  );
}
